import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { clock } from '../../contexts/FarmStatusContext';
import { rackPhotosAPI, getApiBaseUrl } from '../../services/api';
import { systemForPlant } from '../../config/systems';
import { Panel, PanelTitle, Note } from './ui';

/* First photo large, the rest as a tidy grid beside and below it. */
const Mosaic = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 6px;

  &.big > a:first-child { grid-column: span 2; grid-row: span 2; aspect-ratio: auto; }

  @media (max-width: 1100px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    > a { flex: 0 0 44%; scroll-snap-align: start; }
    &.big > a:first-child { flex-basis: 72%; aspect-ratio: 4 / 3; }
  }
`;

const Photo = styled(Link)`
  position: relative;
  display: block;
  min-width: 0;
  min-height: 0;
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  background: ${p => p.theme.colors.surfaceHover};
  color: #fff;
  text-decoration: none;

  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.4s ease; }
  &:hover img { transform: scale(1.03); }
  .c {
    position: absolute; left: 0; right: 0; bottom: 0;
    padding: 18px 8px 6px;
    font-size: 11px;
    line-height: 1.25;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  }
  .c b { font-weight: 500; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) { img { transition: none; } &:hover img { transform: none; } }
`;

/* A filmstrip of the newest rack photos, topped up with plant-log photos. */
export default function RackPhotosStrip({ rackPhotos, logs, now, order }) {
  /* --- newest photos: one per rack from the latest photo day, then plant logs */
  const photos = useMemo(() => {
    const out = [];
    const day = (rackPhotos || [])[0];
    (day?.racks || []).forEach((r) => {
      const p = r.photos?.[0];
      if (p) {
        out.push({
          key: `r${p.id}`, src: rackPhotosAPI.src(p.thumb), large: rackPhotosAPI.src(p.medium),
          to: '/rack-photos', title: r.name, sub: clock(p.uploaded_at, now),
        });
      }
    });
    const seen = new Set();
    (logs || []).forEach((l) => {
      if (out.length >= 10 || !l.images?.length || seen.has(l.plant_id)) return;
      seen.add(l.plant_id);
      const sys = systemForPlant(l.plant_id);
      out.push({
        key: `p${l.id}`,
        src: `${getApiBaseUrl()}/plant-log/image/thumb/${l.images[0]}`,
        large: `${getApiBaseUrl()}/plant-log/image/${l.images[0]}`,
        to: `/plant/${l.plant_id}`,
        title: `${sys ? `${sys.name}, ` : ''}plant ${l.plant_id}`,
        sub: `${l.health_status && l.health_status !== 'healthy' ? `${l.health_status}, ` : ''}${clock(l.timestamp, now)}`,
      });
    });
    return out.slice(0, 10);
  }, [rackPhotos, logs, now]);

  const rest = photos;
  // Exactly as many cells as photos: a large one plus five fills the 3-column grid
  // (2x2 + 2 beside + 3 below); otherwise whole rows of three, or one row of what is left.
  // One strip of five across the wide column: a filmstrip of the racks.
  const mosaic = rest.slice(0, 5);

  return (
    <Panel $order={order}>
      <PanelTitle>From the racks <Link to="/rack-photos">All photos</Link></PanelTitle>
      {photos.length === 0 ? (
        <Note>No photos this week. Upload today's rack photos on Rack Photos.</Note>
      ) : (
        <Mosaic style={{ gridTemplateColumns: `repeat(${Math.max(mosaic.length, 3)}, minmax(0, 1fr))` }}>
          {mosaic.map(p => (
            <Photo key={p.key} to={p.to}>
              <img src={p.src} alt={p.title} loading="lazy" />
              <div className="c"><b>{p.title}</b><br />{p.sub}</div>
            </Photo>
          ))}
        </Mosaic>
      )}
    </Panel>
  );
}
